import React, { useState, useEffect } from 'react';

import Primarytheme from './theme';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

import CloseIcon from '@mui/icons-material/Close';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { TableVirtuoso } from 'react-virtuoso';

import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { Snackbar } from '@mui/material';

import { styled } from '@mui/material/styles';
import { Grid, Typography, Stack, Checkbox, FormControlLabel, FormGroup, Button } from '@mui/material';

import ProjectDisable from './ProjectDisable';
import ProjectParameters from './ProjectParameters';

import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';

import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';

import * as FileSaver from 'file-saver'

import { getSessionIdToken } from '../auth/apiAuth'


import axios from 'axios';

const Item = styled(Paper)(({ theme }) => ({
    ...theme.typography.body2,
    padding: theme.spacing(2),
    textAlign: 'left',
    color: Primarytheme.palette.primary.main,
    fontSize: '16px',
    fontWeight: 'bold',
    background: 'white',
    border: '1px',
    borderRadius: '10px',
    minWidth: 950
}));

const {
    REACT_APP_USER_ID,
} = process.env;


export default function ProjectInfoChart({ auth, selectedProject }) {
    const color = '#4287f5'
    const monthLabel = {
        1: 'Jan',
        2: 'Feb',
        3: 'Mar',
        4: 'Apr',
        5: 'May',
        6: 'Jun',
        7: 'Jul',
        8: 'Aug',
        9: 'Sep',
        10: 'Oct',
        11: 'Nov',
        12: 'Dec'
    }

    const MAX_MONTHS = 24;

    const [monthsRange, setMonthsRange] = useState(12);
    const [fetchingData, setFetchingData] = useState(false);
    const [fullPlotData, setFullPlotData] = useState([]);
    const [fullTableData, setFullTableData] = useState([]);
    const [plotData, setPlotData] = useState([]);
    const [plotDataBar, setPlotDataBar] = useState("");
    const [tableData, setTableData] = useState([]);


    const fetchData = async () => {
        console.log('fetching pagination processes');
        setFetchingData(true);

        if (!selectedProject) {
            setFetchingData(false);
            return;
        }

        const plotDataTemp = [];
        const monthKeys = [];

        for (let t = 0; t < MAX_MONTHS + 1; t++) {
            let date = new Date();
            date.setMonth(date.getMonth() - t);

            const month = monthLabel[date.getMonth() + 1];
            const year = date.getFullYear();
            const label = `${month} ${year}`;

            monthKeys.push(label);
            plotDataTemp.push({ name: label });
        }

        const token = await getSessionIdToken()
        const promises = monthKeys.map((label, idx) => {
            let today = new Date();
            let date = new Date();
            date.setMonth(today.getMonth() - idx);

            let datestart = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
            let dateend;

            if (idx === 0) {
                dateend = new Date().getTime();
            } else {
                dateend = new Date(date.getFullYear(), date.getMonth() + 1, 0).getTime();
            }

            return axios({
                method: "get",
                url: `${process.env.REACT_APP_API_BASE_URL}/operations`,
                headers: {
                    'user_id': REACT_APP_USER_ID || auth.userState.ssmParams.USER_ID,
                    "content-type": "application/json",
                    "Authorization": token,
                    "is_admin": 'false',
                    "project_id": selectedProject,
                    "datestart": datestart,
                    "dateend": dateend,
                    "stati": 'tutti',
                    "show_deleted": 'true',
                    "filter_user": (auth.userState.policy.filterUser) ? auth.userState.email : false
                }
            })
                .then(resp => ({
                    label,
                    items: resp.data.items
                }))
                .catch(err => {
                    console.error('Data fetching error');
                    return { label, items: [] };
                });
        });

        const responses = await Promise.all(promises);

        const tableTemp = [];
        const projectKey = selectedProject.includes('/')
            ? selectedProject.split('/')[1]
            : selectedProject;

        responses.forEach(({ label, items }) => {
            let count = 0;

            items.forEach(process => {
                count++;

                tableTemp.push({
                    id: process.reqId,
                    projectId: process.project_id,
                    date: new Date(process.startTime).toLocaleString(),
                    user: process.owner && typeof (process.owner) === 'object' ? process.owner.email : process.owner,
                    deleted: process.deleted ? 'Yes' : 'No',
                    internalTest: process.internalTest ? 'Yes' : 'No',
                    dateEpoch: process.startTime,
                    originShort: process.originShort
                });
            });

            const idx = plotDataTemp.findIndex(m => m.name === label);
            if (idx !== -1) {
                plotDataTemp[idx][projectKey] = count;
            }
        });

        tableTemp.sort((a, b) => b.dateEpoch - a.dateEpoch);

        const reversed = plotDataTemp.reverse();
        setFullPlotData(reversed);
        setFullTableData(tableTemp);
        setPlotDataBar(selectedProject);

        const sliced = reversed.slice(reversed.length - (monthsRange + 1));
        setPlotData(sliced);
        setTableData(tableTemp);

        setFetchingData(false);
    };

    useEffect(() => {
        if (fullPlotData.length === 0) return;
        const sliced = fullPlotData.slice(fullPlotData.length - (monthsRange + 1));
        setPlotData(sliced);

        const cutoffDate = new Date();
        cutoffDate.setMonth(cutoffDate.getMonth() - monthsRange);
        const cutoffEpoch = cutoffDate.getTime();
        setTableData(fullTableData.filter(row => row.dateEpoch >= cutoffEpoch));
    }, [monthsRange]);

    useEffect(() => {
        const run = async () => {
            if (selectedProject) {
                await fetchData();
            }
        };

        run();
    }, [selectedProject]);

    return (
        <Grid size={12}>
            <Item elevation={3} key='project-countChart'>
                <Typography variant='h5' sx={{ marginBottom: '1em', ml: '0.8em' }} >
                    <b>Project Info Chart</b>
                </Typography>
                <Stack direction='row' spacing={2} sx={{ ml: '1em' }} alignItems="center">
                    <FormControl size='small' sx={{ minWidth: 140 }}>
                        <Select
                            value={monthsRange}
                            onChange={(e) => setMonthsRange(e.target.value)}
                        >
                            <MenuItem value={12}>Last 12 months</MenuItem>
                            <MenuItem value={18}>Last 18 months</MenuItem>
                            <MenuItem value={24}>Last 24 months</MenuItem>
                        </Select>
                    </FormControl>
                    <Button variant='contained' onClick={async () => { await fetchData() }}>
                        reload plot
                    </Button>
                    {fetchingData && (<CircularProgress />)}
                </Stack>
                <Box sx={{ mb: 3 }}>
                    <ResponsiveContainer width="100%" height={600}>
                        <BarChart
                            data={plotData}
                            margin={{ top: 40, right: 20, bottom: 40, left: 0 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="name"
                                interval={0}
                                tick={({ x, y, payload }) => {
                                    const [month, year] = payload.value.split(' ');
                                    return (
                                        <text x={x} y={y} textAnchor="middle" fill="#666" fontSize={12}>
                                            <tspan x={x} dy="0.8em">{month}</tspan>
                                            <tspan x={x} dy="1.2em">{year}</tspan>
                                        </text>
                                    );
                                }}
                                height={60}
                            />
                            <Tooltip />
                            <Bar dataKey={plotDataBar.split('/')[1]} fill={color}>
                                <LabelList dataKey={plotDataBar.split('/')[1]} position="top" fill="#333" fontSize={12} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Box>

                <InfoProjectTable tableData={tableData} selectedProject={selectedProject} />
            </Item>
        </Grid>
    )
}

function InfoProjectTable({ tableData, selectedProject }) {
    const [order, setOrder] = useState('Date');
    const [searchText, setSearchText] = useState('');
    const [tableDataPrint, setTableDataPrint] = useState(tableData);

    useEffect(() => {
        setTableDataPrint(tableData);
        setSearchText('');
    }, [tableData]);

    const handleChangeOrder = (event) => {
        setOrder(event.target.value);
        const oderType = event.target.value;
        switch (oderType) {
            case 'Source data':
                tableDataPrint.sort((a, b) => a.originShort.localeCompare(b.originShort));
                setTableDataPrint(tableDataPrint);
                break;
            case 'User':
                tableDataPrint.sort((a, b) => a.user.localeCompare(b.user));
                setTableDataPrint(tableDataPrint);
                break;
            default:
                tableDataPrint.sort((a, b) => b.dateEpoch - a.dateEpoch);
                setTableDataPrint(tableDataPrint);
        }
    };

    const renderPagProcessCell = (row) => {
        return (
            <Stack direction={'row'}>
                <Box>{row.id}</Box>
                {row.deleted === 'No' && (
                    <IconButton
                        aria-label="open process"
                        onClick={() => {
                            const split = row.date.split(',')[0].split('/')
                            const splittime = row.date.split(',')[1].trim().split(':')
                            const dateS = new Date(`${split[2]}-${split[1]}-${split[0]}T${splittime[0]}:${splittime[1]}:${splittime[2]}Z`)
                            dateS.setHours(dateS.getHours() - 3)
                            const dateStart = dateS.toISOString()
                            const dateEnd = `${split[2]}-${split[1]}-${split[0]}T${splittime[0]}:${splittime[1]}:${splittime[2]}Z`
                            //console.log(dateStart, dateEnd)
                            window.open(`/history/?reqid=${row.id}&dateStart=${Date.parse(dateStart)}&dateEnd=${Date.parse(dateEnd)}`)
                        }
                        }
                    >
                        <LaunchRoundedIcon />
                    </IconButton>
                )}
            </Stack>
        );
    };

    const columns = [
        {
            width: '35%',
            label: 'Source data',
            dataKey: 'originShort',
        },
        {
            width: '15%',
            label: 'Date',
            dataKey: 'date',
        },
        {
            width: '20%',
            label: 'User',
            dataKey: 'user',
        },
        {
            width: '10%',
            label: 'Deleted',
            dataKey: 'deleted',
        },
        {
            width: '20%',
            label: 'Pagination process',
            dataKey: 'id',
            component: renderPagProcessCell
        }
    ];

    const VirtuosoTableComponents = {
        Scroller: React.forwardRef((props, ref) => (
            <TableContainer component={Paper} {...props} ref={ref} />
        )),
        Table: (props) => (
            <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
        ),
        TableHead,
        TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
        TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
    };

    function fixedHeaderContent() {
        return (
            <TableRow>
                {columns.map((column) => (
                    <TableCell
                        key={column.dataKey}
                        variant="head"
                        align={column.numeric || false ? 'right' : 'left'}
                        style={{ width: column.width }}
                        sx={{
                            backgroundColor: 'background.paper',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {column.label}
                    </TableCell>
                ))}
            </TableRow>
        );
    }

    function rowContent(_index, row) {
        return (
            <React.Fragment>
                {columns.map((column) => (
                    <TableCell
                        key={column.dataKey}
                        align={column.numeric || false ? 'right' : 'left'}
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordBreak: 'break-word',
                        }}
                    >
                        {column.component
                            ? column.component(row)
                            : row[column.dataKey]
                        }
                    </TableCell>
                ))}
            </React.Fragment>
        );
    }

    function ExportCsv({ csvData, fileName }) {
        const exportToCsv = () => {
            if (!csvData || !csvData.length) return;
            const headers = Object.keys(csvData[0]);
            const csvRows = [
                headers.join(','),
                ...csvData.map(row =>
                    headers.map(h => {
                        const val = row[h] == null ? '' : String(row[h]);
                        return val.includes(',') || val.includes('"') || val.includes('\n')
                            ? `"${val.replace(/"/g, '""')}"` : val;
                    }).join(',')
                )
            ];
            const data = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=UTF-8' });
            FileSaver.saveAs(data, fileName + '.csv');
        }

        return (
            <Button variant='contained' onClick={exportToCsv}>CSV Export</Button>
        )
    }

    return (
        (tableData.length !== 0 &&
            <>
                <Stack direction='row' spacing={2} justifyContent={'space-between'} alignItems="center" sx={{ marginRight: '1em', marginLeft: '1em' }}>
                    <Typography variant='h5' >
                        <b>Table</b>
                    </Typography>
                </Stack>
                <Stack direction='row' spacing={2} alignItems="center" sx={{ marginBottom: '1em', marginRight: '1em', marginLeft: '1em' }}>
                    <TextField
                        size="small"
                        placeholder="Search..."
                        value={searchText}
                        onChange={(e) => {
                            const val = e.target.value;
                            setSearchText(val);
                            if (!val) {
                                setTableDataPrint(tableData);
                            } else {
                                const lower = val.toLowerCase();
                                setTableDataPrint(tableData.filter(row =>
                                    row.id.toLowerCase().includes(lower) ||
                                    row.date.toLowerCase().includes(lower) ||
                                    row.user.toLowerCase().includes(lower) ||
                                    (row.originShort || '').toLowerCase().includes(lower)
                                ));
                            }
                        }}
                        sx={{ minWidth: 200 }}
                        InputProps={{
                            endAdornment: searchText && (
                                <IconButton size="small" onClick={() => { setSearchText(''); setTableDataPrint(tableData); }}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            )
                        }}
                    />
                    <Typography >
                        <b>Order by:</b>
                    </Typography>
                    <FormControl size='small' sx={{ minWidth: 120 }}>
                        <Select
                            defaultValue="Date"
                            displayEmpty
                            id="order-select"
                            value={order}
                            onChange={handleChangeOrder}
                        >
                            <MenuItem value="Date">Date</MenuItem>
                            <MenuItem value="Source data">Source Data</MenuItem>
                            <MenuItem value="User">User</MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{ flexGrow: 1 }} />
                    <ExportCsv csvData={tableDataPrint} fileName={selectedProject + '_export'} />
                </Stack>
                <Paper style={{ height: 400, width: '100%' }} >
                    <TableVirtuoso
                        data={tableDataPrint}
                        components={VirtuosoTableComponents}
                        fixedHeaderContent={fixedHeaderContent}
                        itemContent={rowContent}
                    />
                </Paper>
            </>
        )
    )
}